// src/components/homepage/Login.js
//
// Email-first login experience:
//   1. The user enters their email and continues.
//   2. We ask the backend (getPublicAccountState) what to show next:
//        - has_password  -> password step (normal login)
//        - needs_setup    -> account setup step (student creates a password)
//        - needs_reset    -> guidance to reset via email
//        - not_found      -> guidance to sign up
//   3. Password reset is available from the password step for existing accounts.
//
// This never exposes account details beyond what's needed to route the user,
// and inline password creation only works for passwordless student accounts
// (enforced server-side).

import React, { useState } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { auth, functions } from "../../firebaseConfig";

const styles = {
  page: {
    minHeight: "100vh",
    padding: "40px 16px",
    background: "radial-gradient(circle at top, #e0f2fe 0, #eef2ff 40%, #f9fafb 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  card: {
    width: "100%",
    maxWidth: "430px",
    borderRadius: "24px",
    padding: "26px 26px 28px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 26px 70px rgba(15,23,42,0.14)",
  },
  header: { marginBottom: "20px" },
  title: { fontSize: "22px", fontWeight: 700, color: "#111827", margin: 0 },
  subtitle: { fontSize: "13px", color: "#6b7280", marginTop: "6px", lineHeight: 1.5 },
  formGroup: { marginBottom: "16px" },
  label: {
    display: "block",
    fontSize: "13px",
    color: "#4b5563",
    marginBottom: "6px",
    fontWeight: 500,
  },
  emailDisplay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "9px 14px",
    borderRadius: "999px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    fontSize: "13px",
    color: "#334155",
    marginBottom: "16px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "999px",
    border: "1px solid #d1d5db",
    fontSize: "13px",
    outline: "none",
    backgroundColor: "#f9fafb",
    boxSizing: "border-box",
  },
  button: {
    width: "100%",
    marginTop: "10px",
    padding: "11px 16px",
    borderRadius: "999px",
    border: "none",
    background: "linear-gradient(135deg, #2563eb, #1e40ff)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 18px 40px rgba(37,99,235,0.55)",
  },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
  },
  errorBox: {
    marginTop: "12px",
    padding: "8px 10px",
    borderRadius: "10px",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    fontSize: "12px",
    color: "#b91c1c",
  },
  infoBox: {
    marginTop: "12px",
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    fontSize: "12px",
    color: "#1e40af",
    lineHeight: 1.5,
  },
  helper: {
    marginTop: "14px",
    fontSize: "12px",
    color: "#a3a3a3",
    textAlign: "center",
    lineHeight: 1.5,
  },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
  },
};

const friendlyAuthError = (code, fallback) => {
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect password. Please try again or reset your password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a moment and try again.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact us for help.";
    default:
      return fallback || "Something went wrong. Please try again.";
  }
};

export default function Login() {
  const navigate = useNavigate();

  // step: "email" | "password" | "setup" | "reset_hint" | "not_found"
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const resetMessages = () => {
    setError("");
    setInfo("");
  };

  const backToEmail = () => {
    resetMessages();
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setStep("email");
  };

  // --- Step 1: email -> account state ---
  const handleEmailContinue = async (e) => {
    e.preventDefault();
    resetMessages();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setEmail(trimmed);
    setBusy(true);
    try {
      const checkState = httpsCallable(functions, "getPublicAccountState");
      const res = await checkState({ email: trimmed });
      const state = res?.data?.state;
      if (state === "has_password") {
        setStep("password");
      } else if (state === "needs_setup") {
        setStep("setup");
      } else if (state === "needs_reset") {
        setStep("reset_hint");
      } else {
        setStep("not_found");
      }
    } catch (err) {
      setError(err?.message || "Could not check that email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // --- Step 2a: normal password login ---
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // App.js onAuthStateChanged handles routing to the right dashboard.
    } catch (err) {
      setError(friendlyAuthError(err?.code, err?.message));
      setBusy(false);
    }
  };

  // --- Step 2b: first-time account setup (inline create password) ---
  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const complete = httpsCallable(functions, "completeStudentAccountSetup");
      await complete({ email, password: newPassword });
      // Now sign in with the freshly-set password.
      await signInWithEmailAndPassword(auth, email, newPassword);
      // App.js routing takes over from here.
    } catch (err) {
      setError(err?.message || "Could not set up your account. Please try again.");
      setBusy(false);
    }
  };

  // --- Password reset (existing accounts only) ---
  const handleSendReset = async () => {
    resetMessages();
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo(
        `We've sent a password reset link to ${email}. Check your inbox (and spam folder) to choose a new password.`
      );
    } catch (err) {
      // Keep this generic to avoid leaking account details.
      setInfo(
        `If an account exists for ${email}, a password reset link has been sent. Check your inbox and spam folder.`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* ---------- STEP: EMAIL ---------- */}
        {step === "email" && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>Sign in</h2>
              <p style={styles.subtitle}>Enter your email to continue.</p>
            </div>
            <form onSubmit={handleEmailContinue}>
              <div style={styles.formGroup}>
                <label htmlFor="email" style={styles.label}>Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  style={styles.input}
                />
              </div>
              <button type="submit" disabled={busy} style={{ ...styles.button, ...(busy ? styles.buttonDisabled : {}) }}>
                {busy ? "Checking…" : "Continue"}
              </button>
            </form>
            {error && <div style={styles.errorBox}>{error}</div>}
            <p style={styles.helper}>
              New here?{" "}
              <button type="button" style={styles.linkBtn} onClick={() => navigate("/signup")}>
                Create an account
              </button>
            </p>
          </>
        )}

        {/* ---------- STEP: PASSWORD ---------- */}
        {step === "password" && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>Welcome back</h2>
              <p style={styles.subtitle}>Enter your password to sign in.</p>
            </div>
            <div style={styles.emailDisplay}>
              <span>{email}</span>
              <button type="button" style={styles.linkBtn} onClick={backToEmail}>Change</button>
            </div>
            <form onSubmit={handlePasswordLogin}>
              <div style={styles.formGroup}>
                <label htmlFor="password" style={styles.label}>Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  autoFocus
                  style={styles.input}
                />
              </div>
              <button type="submit" disabled={busy} style={{ ...styles.button, ...(busy ? styles.buttonDisabled : {}) }}>
                {busy ? "Signing in…" : "Log In"}
              </button>
            </form>
            <div style={styles.rowBetween}>
              <span />
              <button type="button" style={styles.linkBtn} onClick={handleSendReset} disabled={busy}>
                Forgot password?
              </button>
            </div>
            {error && <div style={styles.errorBox}>{error}</div>}
            {info && <div style={styles.infoBox}>{info}</div>}
          </>
        )}

        {/* ---------- STEP: ACCOUNT SETUP ---------- */}
        {step === "setup" && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>Set up your account</h2>
              <p style={styles.subtitle}>
                Welcome! Create a password to finish setting up your account, then you can sign in and
                take diagnostic tests.
              </p>
            </div>
            <div style={styles.emailDisplay}>
              <span>{email}</span>
              <button type="button" style={styles.linkBtn} onClick={backToEmail}>Change</button>
            </div>
            <form onSubmit={handleSetupSubmit}>
              <div style={styles.formGroup}>
                <label htmlFor="newPassword" style={styles.label}>Create password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label htmlFor="confirmPassword" style={styles.label}>Confirm password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  style={styles.input}
                />
              </div>
              <button type="submit" disabled={busy} style={{ ...styles.button, ...(busy ? styles.buttonDisabled : {}) }}>
                {busy ? "Setting up…" : "Create password & sign in"}
              </button>
            </form>
            {error && <div style={styles.errorBox}>{error}</div>}
          </>
        )}

        {/* ---------- STEP: RESET HINT (passwordless non-student) ---------- */}
        {step === "reset_hint" && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>Set your password</h2>
              <p style={styles.subtitle}>
                For security, we&rsquo;ll email you a secure link to set your password.
              </p>
            </div>
            <div style={styles.emailDisplay}>
              <span>{email}</span>
              <button type="button" style={styles.linkBtn} onClick={backToEmail}>Change</button>
            </div>
            <button
              type="button"
              onClick={handleSendReset}
              disabled={busy}
              style={{ ...styles.button, marginTop: 0, ...(busy ? styles.buttonDisabled : {}) }}
            >
              {busy ? "Sending…" : "Email me a set-password link"}
            </button>
            {info && <div style={styles.infoBox}>{info}</div>}
            {error && <div style={styles.errorBox}>{error}</div>}
          </>
        )}

        {/* ---------- STEP: NOT FOUND ---------- */}
        {step === "not_found" && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>No account found</h2>
              <p style={styles.subtitle}>
                We couldn&rsquo;t find an account for <strong>{email}</strong>.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/signup")}
              style={{ ...styles.button, marginTop: 0 }}
            >
              Sign up to get started
            </button>
            <div style={styles.rowBetween}>
              <button type="button" style={styles.linkBtn} onClick={backToEmail}>Try a different email</button>
              <span />
            </div>
            <p style={styles.helper}>
              If you believe you already have an account, please contact your tutor or program
              coordinator.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
